import { useState, useEffect } from 'react';
import {
  ShoppingBag,
  Smartphone,
  Shirt,
  Home,
  Dumbbell,
  BookOpen,
  Car,
  Sparkles,
  Gamepad2,
} from 'lucide-react';

interface SplashScreenProps {
  onComplete: () => void;
}

const bgIcons = [
  { Icon: Smartphone, top: '8%', left: '10%' },
  { Icon: Shirt, top: '15%', right: '12%' },
  { Icon: Home, top: '35%', left: '5%' },
  { Icon: Dumbbell, top: '30%', right: '8%' },
  { Icon: BookOpen, bottom: '35%', left: '8%' },
  { Icon: Car, bottom: '30%', right: '10%' },
  { Icon: Sparkles, bottom: '12%', left: '15%' },
  { Icon: Gamepad2, bottom: '15%', right: '15%' },
  { Icon: ShoppingBag, top: '50%', left: '3%' },
  { Icon: Smartphone, top: '55%', right: '5%' },
];

export function SplashScreen({ onComplete }: SplashScreenProps) {
  const [phase, setPhase] = useState<'initial' | 'logo' | 'text' | 'complete'>('initial');

  useEffect(() => {
    // Animation sequence
    const timers = [
      setTimeout(() => setPhase('logo'), 100),
      setTimeout(() => setPhase('text'), 600),
      setTimeout(() => setPhase('complete'), 2200),
      setTimeout(() => onComplete(), 2800),
    ];

    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div
      className={`
        fixed inset-0 z-[200]
        bg-gradient-to-br from-primary-600 to-primary-900
        flex flex-col items-center justify-center
        transition-opacity duration-500
        ${phase === 'complete' ? 'opacity-0' : 'opacity-100'}
      `}
    >
      {/* Background Icons */}
      {bgIcons.map(({ Icon, ...position }, index) => (
        <div
          key={index}
          className="absolute text-secondary-400/30"
          style={position}
        >
          <Icon className="w-12 h-12" strokeWidth={1.5} />
        </div>
      ))}

      {/* Logo */}
      <div
        className={`
          transition-all duration-700 ease-out
          ${phase === 'initial' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'}
        `}
      >
        <img src="/logo.png" alt="NimBazaar" className="h-64 w-auto drop-shadow-2xl" />
      </div>

      {/* Loading bar */}
      <div
        className={`
          absolute bottom-16 left-1/2 -translate-x-1/2 w-48
          transition-opacity duration-300
          ${phase === 'text' || phase === 'complete' ? 'opacity-100' : 'opacity-0'}
        `}
      >
        <div className="h-0.5 bg-primary-400/40 rounded-full overflow-hidden">
          <div
            className={`
              h-full bg-secondary-400 rounded-full
              transition-all duration-[1600ms] ease-out
              ${phase === 'text' || phase === 'complete' ? 'w-full' : 'w-0'}
            `}
          />
        </div>
      </div>
    </div>
  );
}
